import React from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useSpotlightsQuery } from '../../../../api/spotlights';

import { useTranslation } from 'react-i18next';

const LIST_ITEM_CLASS =
  'group flex w-full items-center gap-3 rounded-lg pl-0 pr-3 py-2.5 text-left text-sm transition-all hover:bg-gray-50 dark:hover:bg-gray-800 hover:shadow-sm';
const ICON_WRAPPER_CLASS =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 group-hover:bg-violet-100 group-hover:text-violet-700';

export const SpotlightsHoverContent: React.FC = () => {
  const { t } = useTranslation('pleri/chat');
  const { data, isLoading, error } = useSpotlightsQuery();
  const spotlights = data?.data?.spotlights ?? [];

  if (isLoading) {
    return (
      <div className="flex w-max max-w-[360px] min-w-[260px] flex-col items-center justify-center gap-2 px-4 py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-violet-500 dark:border-gray-700" />
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {t('loadingSpotlights')}
        </span>
      </div>
    );
  }

  if (error || spotlights.length === 0) {
    return (
      <div className="w-max max-w-[360px] min-w-[260px] px-4 py-6 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {spotlights.length === 0
            ? t('noSpotlightsYet')
            : t('couldNotLoadSpotlights')}
        </p>
        <Link
          to="/spotlights"
          className="mt-3 inline-flex items-center text-sm font-medium text-violet-600 hover:text-violet-700"
        >
          {t('goToSpotlights')}
        </Link>
      </div>
    );
  }

  return (
    <div className="w-max max-w-[360px] min-w-[260px]">
      <div className="space-y-0.5 py-0.5">
        {spotlights.slice(0, 8).map((spotlight) => (
          <Link
            key={spotlight.id}
            to="/spotlights"
            className={LIST_ITEM_CLASS}
            data-testid={`spotlight-hover-item-${spotlight.id}`}
          >
            <span className={ICON_WRAPPER_CLASS}>
              <Sparkles className="h-4 w-4 stroke-[1.8]" />
            </span>
            <span className="min-w-0 flex-1 truncate font-medium text-gray-900 dark:text-gray-100">
              {spotlight.message || spotlight.spotlightType}
            </span>
          </Link>
        ))}
      </div>
      <div className="mt-3 flex justify-end border-t border-gray-100 pt-3 dark:border-gray-800">
        <Link
          to="/spotlights"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-600 transition-colors hover:text-orange-700"
          data-testid="spotlights-view-all"
        >
          {t('viewAll')}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
};
